// Password validation utilities
// These validations match the backend implementation in gateway/internal/auth/password.go

const MIN_PASSWORD_LENGTH = 8
const MAX_PASSWORD_LENGTH = 72 // bcrypt limit

// Common passwords list - matches backend
const COMMON_PASSWORDS = [
  'password',
  'password1',
  'password123',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty123',
  'qwertyuiop',
  'letmein1',
  'welcome1',
  'admin123',
  'iloveyou',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'dragon123',
  'master123',
  'monkey123',
  'shadow123',
  'abc12345',
  'abcd1234',
  'abcdefgh',
]

export interface PasswordValidationError {
  // Machine-readable error code
  code: string
  // User-friendly error message
  message: string
}

export interface PasswordValidationResult {
  valid: boolean
  errors: PasswordValidationError[]
}

/**
 * Validates a password against all requirements.
 * Returns a result with all validation errors (not just the first one).
 * This implementation matches the backend validation in gateway/internal/auth/password.go
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: PasswordValidationError[] = []

  // Check empty
  if (!password) {
    errors.push({
      code: 'password_empty',
      message: 'Password is required',
    })
    return { valid: false, errors }
  }

  // Check minimum length
  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push({
      code: 'password_too_short',
      message: 'Password must be at least 8 characters',
    })
  }

  // Check maximum length (bcrypt limit)
  if (password.length > MAX_PASSWORD_LENGTH) {
    errors.push({
      code: 'password_too_long',
      message: 'Password must be at most 72 characters',
    })
  }

  // Check for at least one letter
  const hasLetter = /[a-zA-Z]/.test(password)
  if (!hasLetter) {
    errors.push({
      code: 'password_no_letter',
      message: 'Password must contain at least one letter',
    })
  }

  // Check for at least one number
  const hasNumber = /[0-9]/.test(password)
  if (!hasNumber) {
    errors.push({
      code: 'password_no_number',
      message: 'Password must contain at least one number',
    })
  }

  // Check for common weak passwords
  if (isCommonPassword(password)) {
    errors.push({
      code: 'password_too_common',
      message: 'Password is too common. Please choose a stronger password',
    })
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Checks if the password is in a list of common passwords.
 */
function isCommonPassword(password: string): boolean {
  const lower = password.toLowerCase()
  return COMMON_PASSWORDS.includes(lower)
}

/**
 * Formats validation errors into a single user-friendly message.
 * Useful for displaying in forms.
 */
export function formatValidationErrors(
  result: PasswordValidationResult
): string {
  if (result.valid) {
    return ''
  }
  return result.errors.map((e) => e.message).join('. ')
}
